import * as React from "react";
import AppBarMui from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import LanguageIcon from "@mui/icons-material/Language";
import Container from "@mui/material/Container";
import MenuItem from "@mui/material/MenuItem";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import { Divider, Link, ListSubheader } from "@mui/material";

import useOnlineStatus from "../../hooks/useOnlineStatus";
import useIsRoleAdmin from "../../hooks/useIsRoleAdmin";
import useAuthorizedPages from "../../hooks/useAuthorizedPages";
import useIsSignedIn from "../../hooks/useIsSignedIn";
import LanguageSwitcher from "../LanguageSwitcher";
import useI18n from "../../hooks/useI18n";

export interface AppBarProps {
  title: string;
  menuItems: NavigationItem[];
  showMenu: boolean | undefined;
}

export interface LinkItem {
  kind: "item";
  title: string;
  pageId: string;
}

export interface GroupItem {
  kind: "group";
  title: string;
  items: LinkItem[];
}

export type NavigationItem = LinkItem | GroupItem;

const AppBar: React.FC<AppBarProps> = ({ title, menuItems, showMenu }) => {
  const isOnline = useOnlineStatus();
  const isRoleAdmin = useIsRoleAdmin();
  const isSignedIn = useIsSignedIn();
  const { authorizedPages } = useAuthorizedPages();
  const { t } = useI18n();

  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElLanguage, setAnchorElLanguage] =
    React.useState<null | HTMLElement>(null);

  const authorizedMenuItems: NavigationItem[] = React.useMemo(() => {
    if (!authorizedPages) return [];
    
    return menuItems.reduce<NavigationItem[]>((acc, item) => {
      if (item.kind === "group") {
        const filteredItems = item.items.filter((subItem) => 
          authorizedPages.includes(subItem.pageId)
        );
        if (filteredItems.length > 0) {
          acc.push({ ...item, items: filteredItems });
        }
      } else {
        if (authorizedPages.includes(item.pageId)) {
          acc.push(item);
        }
      }
      return acc;
    }, []);
  }, [menuItems, authorizedPages]);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleOpenLanguageMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElLanguage(event.currentTarget);
  };

  const handleCloseLanguageMenu = () => {
    setAnchorElLanguage(null);
  };

  const renderMenuItem = (item: LinkItem) => (
    <Link
      href={isRoleAdmin ? `/${item.pageId}` : `/${item.pageId}/form`}
      underline="none"
      key={item.pageId}
    >
      <MenuItem onClick={handleCloseNavMenu}>
        <Typography textAlign="center">{item.title}</Typography>
      </MenuItem>
    </Link>
  );

  return (
    <AppBarMui position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <AppRegistrationIcon
            sx={{ display: { xs: "none", md: "flex" }, mr: 1 }}
          />
          <Typography
            variant="h6"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: { xs: "none", md: "flex" },
              fontFamily: "monospace",
              fontWeight: 700,
              letterSpacing: ".3rem",
              color: "inherit",
              textDecoration: "none",
            }}
          >
            {title}
          </Typography>

          <AppRegistrationIcon
            sx={{ display: { xs: "flex", md: "none" }, mr: 1 }}
          />
          <Typography
            variant="h5"
            noWrap
            component="a"
            href=""
            sx={{
              mr: 2,
              display: { xs: "flex", md: "none" },
              flexGrow: 1,
              fontFamily: "monospace",
              fontWeight: 700,
              letterSpacing: ".3rem",
              color: "inherit",
              textDecoration: "none",
            }}
          >
            {title}
          </Typography>
          <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }} />
          <Box sx={{ flexGrow: 0 }}>
            <IconButton
              size="large"
              aria-label="language"
              aria-controls="language-menu"
              aria-haspopup="true"
              onClick={handleOpenLanguageMenu}
              color="inherit"
            >
              <LanguageIcon />
            </IconButton>
            <Menu
              id="language-menu"
              anchorEl={anchorElLanguage}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              open={Boolean(anchorElLanguage)}
              onClose={handleCloseLanguageMenu}
              sx={{
                display: { xs: "block", md: "block" },
              }}
            >
              <LanguageSwitcher />
            </Menu>
          </Box>

          {showMenu && (
            <Box sx={{ flexGrow: 0 }}>
              <IconButton
                size="large"
                aria-label="menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: "block", md: "block" },
                }}
              >
                {authorizedMenuItems.map((item, index) => {
                  if (item.kind === "group") {
                    return (
                      <Box key={item.title}>
                        <ListSubheader 
                          sx={{ 
                            lineHeight: '36px', 
                            fontWeight: 'bold',
                            backgroundColor: 'transparent'
                          }}
                        >
                          {item.title}
                        </ListSubheader>
                        {item.items.map(renderMenuItem)}
                        {index < authorizedMenuItems.length - 1 && <Divider />}
                      </Box>
                    );
                  }
                  return renderMenuItem(item);
                })}

                {isOnline && <Divider />}
                {isOnline && (
                  <Link href="/changepassword" underline="none">
                    <MenuItem onClick={handleCloseNavMenu}>
                      <Typography textAlign="center">
                        {t("auth.change_password")}
                      </Typography>
                    </MenuItem>
                  </Link>
                )}
                {isOnline && !isSignedIn && <Divider />}
                {isOnline && !isSignedIn && (
                  <Link href="/register" underline="none">
                    <MenuItem onClick={handleCloseNavMenu}>
                      <Typography textAlign="center">
                        {t("auth.register")}
                      </Typography>
                    </MenuItem>
                  </Link>
                )}
                {isOnline && <Divider />}
                {isOnline && (
                  <Link href="/signin" underline="none">
                    <MenuItem onClick={handleCloseNavMenu}>
                      <Typography textAlign="center">
                        {isSignedIn ? t("auth.sign_out") : t("auth.sign_in")}
                      </Typography>
                    </MenuItem>
                  </Link>
                )}
              </Menu>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBarMui>
  );
};

export default AppBar;
